import {
    componentName,
    componentClasses,
    defaultProps,
    eventMap,
    views,
} from "../../../core/components/loading-indicator/loading-indicator";
import {
    createStore,
    renderState,
    dispatchEvent,
    reducers,
    actions,
} from "../../../core/js/state-machine";
import { buildFocusTrap } from "../../../core/js/focus-trap/focus-trap";
import validateInitialization from "../../../core/js/helpers/validate-initialization";

/**
 * Though the loading indicator does not necessarily require a wrapper,
 * if this js is leveraged, then it implies that the user intent to use loading indicator with overlay.
 * a wrapper is required in that case.
 * @param {HTMLElement} LoadingIndicator main container
 * @returns {Boolean}
 *
 * @todo provide a validateHTML function to the validateInitialization
 */
const validateHTML = (element) => {
    const wrapperElement = element.querySelector(`.${componentClasses.wrapper}`);
    const loadingIndicatorElement = element.querySelector(`.${componentClasses.main.base}`);
    const labelElement = element.querySelector(`.${componentClasses.label}`);
    const overlayElement = element.querySelector(`.${componentClasses.overlay.base}`);

    return wrapperElement && loadingIndicatorElement && labelElement && overlayElement
        ? { wrapperElement, loadingIndicatorElement, labelElement, overlayElement }
        : false;
};

/**
 * Initialize the LoadingIndicator component
 * @param {HTMLElement} element main LoadingIndicator container
 * @param {defaultProps} props optional settings.
 * @returns {{
 *  element: HTMLElement,
 * }} public api object
 */
const LoadingIndicator = (element, props) => {
    const [success, mergedProps, validElements] = validateInitialization({
        componentName,
        element,
        defaultProps,
        props,
        views,
        validateHTML,
    });
    if (!success) return;

    //Handling the state changes for the component
    const handleStateChange = (visible) => {
        renderState(visible ? views.visible : views.hidden, validElements.overlayElement);
        renderState(visible ? views.visible : views.hidden, validElements.wrapperElement);
        renderState(visible ? views.body.overflowHidden : views.body.base, document.body);
        dispatchEvent(visible ? eventMap.show : eventMap.hide, element);
    };

    //Helper functions to fire the dispatch action to show and hide the overlay
    const show = () => {
        return visibilityStore.dispatch(actions.show());
    };

    const hide = () => {
        setLoadingSrOnlyLabel();
    };

    const dispose = () => {
        focusTrap.dispose();
        element[componentName] = null;
        element = null;
        api = null;
    };

    const setLoadingSrOnlyLabel = () => {
        //Set the completed string
        validElements.labelElement.ariaDescription = mergedProps.srCompletedText;

        setTimeout(() => {
            visibilityStore.dispatch(actions.hide());

            //Remove aria-description after label is hidden
            validElements.labelElement.removeAttribute("aria-description");
        }, 500);
    };

    //Initializing the focusTrap
    const focusTrap = buildFocusTrap(validElements.overlayElement);

    //Initializing the store
    const visibilityStore = createStore(reducers.boolReducer, false);
    visibilityStore.subscribe(handleStateChange);

    let api = {
        element,
        dispose,
        show,
        hide,
    };

    element[componentName] = api;

    return api;
};

export default LoadingIndicator;
